import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

export function ClientOtpLink({ clientId, readOnly, onConnected }) {
  const [st, setSt] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const dirty = useRef(false);
  const load = useCallback(() => api.get(`/clients/${clientId}/otp-link`).then((r) => { setSt(r.data); if (!dirty.current) { setEmail(r.data.contact_email || ""); } }).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (st?.last?.status !== "pending") { return undefined; }
    const t = setInterval(async () => { const r = await api.get(`/clients/${clientId}/otp-link`); setSt(r.data); if (r.data.last?.status === "used") { onConnected(); } }, 5000);
    return () => clearInterval(t);
  }, [st?.last?.status, clientId, onConnected]);
  const saveEmail = async () => {
    try { await api.put(`/clients/${clientId}/contact-email`, { email }); toast.success("Client email saved"); dirty.current = false; load(); } catch (e) { toast.error(apiError(e)); }
  };
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/clients/${clientId}/otp-link`); toast.success(`OTP link emailed to ${data.to}`); load(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!st) { return null; }
  const last = st.last;
  const fmt = (t) => new Date(t).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid="client-otp-link">
      <div className="small-label">Client enters the OTP</div>
      <p className="text-xs text-muted-foreground">GSTN sends the OTP to the client's registered mobile. We email the client a secure link to type it in; the session then lasts 30 days.</p>
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <input data-testid="client-contact-email-input" type="email" value={email} onChange={(e) => { dirty.current = true; setEmail(e.target.value); }} placeholder="client@email"
            className="flex-1 min-w-[180px] rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono" />
          {email !== (st.contact_email || "") && <button data-testid="client-contact-email-save" onClick={saveEmail} className="rounded-md border border-border px-3 py-2 text-xs">Save email</button>}
          <button data-testid="client-otp-link-send" onClick={send} disabled={busy || !st.contact_email || email !== st.contact_email}
            className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-2 text-xs font-medium">
            <Send className="h-3.5 w-3.5" /> {busy ? "Sending…" : "Send OTP to client"}
          </button>
        </div>
      )}
      {readOnly && <p className="text-xs font-mono">{st.contact_email || "No client email saved"}</p>}
      {last && <p className="text-xs font-mono" data-testid="client-otp-link-status">
        Last request {fmt(last.created_at)} by {last.sent_by} to {last.to}: {last.status === "awaiting_request" ? (new Date(last.expires_at) < new Date() ? "renewal link expired" : "renewal link sent, waiting for client") : last.status === "pending" ? (new Date(last.expires_at) < new Date() ? "expired" : "waiting for client") : last.status === "used" ? `client entered OTP ${fmt(last.used_at)}` : last.status}
      </p>}
    </div>
  );
}
